// @flow strict

import type { FitStrategy, Source } from '../types';

/**
 * While on-demand resizing an image based on the container size, if container aspect ratio is similar to original source aspect ratio
 * (in this case difference of aspect ratios is less than 10%) then we keep the original aspect ratio.
 * I don't think we have done much research to determine what the right threshold is.  Someone should research this
 */
const KEEP_ORIGINAL_ASPECT_RATIO_THRESHOLD = 0.1;

/**
 * This method calculates the snap size for the image. It checks stretching to decide the next size.
 * For example 610px with roundStep=50x might be snapped to 600px whereas a 130px might be snapped to 150px.
 */
const calculateSnapSize = (size: number, roundStep: number, allowedStretch: number) => {
  const floorSize = Math.floor(size / roundStep) * roundStep;
  const ceilSize = Math.ceil(size / roundStep) * roundStep;
  const stretchForFloorSize = floorSize === 0 ? 0 : (size - floorSize) / floorSize;

  // if flooring will cause too much stretch, bump up to the next size
  return stretchForFloorSize === 0 || stretchForFloorSize > allowedStretch ? ceilSize : floorSize;
};

const isBetween = (testNum: number, low: number, high: number): boolean =>
  testNum >= low && testNum <= high;

/**
 * Use the dynamic source url to get the size we want rather than one of the pre-cut sizes that exists on the CDN
 */
const getDynamicSource = (
  targetImageWidth: number,
  targetImageHeight: number,
  sources: Array<Source>,
  dynamicSize: Source,
  allowedStretch: number,
  fitStrategy: FitStrategy,
): Source => {
  const allowedStretchOverride = 0.05;
  // Since we are sending the dynamic size in PHOTO_SIZES with a zero/zero size so it will not be used by clients that don't know about it,
  // we will have to infer the original aspect ratio using the largest size we have available
  let largestCut: Source = { url: '', width: 0, height: 0 };
  sources.forEach(src => {
    if (!largestCut || src.width * src.height > largestCut.width * largestCut.height) {
      largestCut = src;
    }
  });

  // we round the width and height to lower 100x if there will not be too much stretch, otherwise we will snap to higher 100x
  const roundStep = 100;
  let width = calculateSnapSize(targetImageWidth, roundStep, allowedStretchOverride);
  let height = calculateSnapSize(targetImageHeight, roundStep, allowedStretchOverride);

  const srcAspectRatio = largestCut.width / largestCut.height;
  const targetAspectRatio = targetImageWidth / targetImageHeight;
  const containerAspectRatioSimilarToOriginalSrc =
    Math.abs(srcAspectRatio - targetAspectRatio) / targetAspectRatio <
    KEEP_ORIGINAL_ASPECT_RATIO_THRESHOLD;

  // Adjust the width/height to retain the original aspect ratio in a few cases
  if (fitStrategy === 'contain') {
    // if fitStrategy is contain, the intent is to show the image at the original aspect ratio
    // We want to return an image at the original aspect ratio, but we have the opportunity to save bandwidth by returning a smaller version
    // contain will make the image the same size or smaller than the target because it will be sized to fit within the placement
    if (targetAspectRatio > srcAspectRatio) {
      // placement is wider than image
      // the ideal height of the image is the same as the height of the placement
      const idealHeight = targetImageHeight;
      const idealWidth = Math.ceil(idealHeight * srcAspectRatio);
      // now turn this into a standard size to improve the cdn cache hit ratio
      width = calculateSnapSize(idealWidth, roundStep, allowedStretchOverride);
    }
    height = -1;
  } else if (containerAspectRatioSimilarToOriginalSrc || fitStrategy === 'calculated') {
    // if fitStrategy is calculated, they are most likely taking aspect ratio into account, so it is best to return the original aspect ratio
    // keep width the same (snapped to roundStep), and float height.
    // This will likely provide a larger image than we need, but we have no idea how the client is going to do it's "calculation"
    height = -1;
  } else if (width <= 100 && height <= 100) {
    // For small images, the roundStep gives versions that are never quite right.  Simply use the original aspect ratio.
    // The file sizes are small at these resolutions, so go for the image that fits the best
    // This is very similar logic as the block above for fitStrategy=contain, but the logic is backwards because this is fitStrategy=cover
    if (targetAspectRatio < srcAspectRatio) {
      // the ideal height of the image is the same as the height of the placement
      const idealHeight = targetImageHeight;
      const idealWidth = Math.ceil(idealHeight * srcAspectRatio);
      // now turn this into a standard size to improve the cdn cache hit ratio
      width = calculateSnapSize(idealWidth, roundStep, allowedStretchOverride);
    }
    height = -1;
  } else {
    // Not using original aspect ratio
    // We often have problem where we lose some of the image because the server crops in one dimension, but the client crops in the other.
    // This results in a photo that looks zoomed-in, which is bad because we want to see as much of the original as possible.
    // In order to fix this problem, use a size that will be cropped in the same dimension on both the server and client.
    // The way to do this is to extend server size slightly in the same dimension that will be cropped on the client.
    const low = Math.min(srcAspectRatio, targetAspectRatio);
    const high = Math.max(srcAspectRatio, targetAspectRatio);
    const requestAspectRatio = width / height;
    if (!isBetween(requestAspectRatio, low, high)) {
      // Move up one roundStep in the right dimension
      if (requestAspectRatio < low) {
        width += roundStep;
      } else {
        height += roundStep;
      }
    }
  }

  // Limit the requested image to be no bigger than the largest available size
  width = Math.min(width, largestCut.width);
  height = Math.min(height, largestCut.height);

  const url = dynamicSize.url.replace('w=0', `w=${width}`).replace('h=0', `h=${height}`);
  // The server api uses height=-1 for original aspect ratio, but some clients of this method use the size for their own purposes (like a calculated fitStrategy)
  // return a calculated height in case the caller uses it.  It's possible this may be a pixel off from the actual image returned by the server (I haven't checked)
  height = height >= 0 ? height : Math.ceil(width * (1 / srcAspectRatio));
  return {
    url,
    width,
    height,
  };
};

export default getDynamicSource;
